import React, { useContext, useEffect, useMemo, useState } from "react";
import ContrySelect from '../../../components/contryselect/ContrySelect'
import countryList from "react-select-country-list";
import toast from "react-hot-toast";
import { Grid, TextField, Typography, Button, Checkbox, FormControlLabel } from "@mui/material";
import { DemoLoginApi } from '../../../api/request/auth';
import { ThemeContext } from "../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { parsePhoneNumberFromString, getCountryCallingCode } from "libphonenumber-js";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import UserDetail from "./UserDetail";
import PhoneNumberInput from "../../../components/contryselect/PhoneNumberInput";


const SignUp = ({ back }) => {
    const { darkMode } = useContext(ThemeContext)
    const [formValues, setFormValues] = useState({ address: "", country: "", deposit: "100000", email: "", name: "", phone: "", nodeID: 2, agree: false, });
    const [isCompanyDetailsVisible, setIsCompanyDetailsVisible] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const { t } = useTranslation()
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const excludedCountries = ['AQ', 'BV', 'TF', "HM", 'PN', 'GS', 'UM'];
    const countries = useMemo(() => {
        return countryList().getData()
            .filter(c => !excludedCountries.includes(c.value))
            .map(c => {
                const callingCode = getCountryCallingCode(c.value);
                return {
                    label: `${c.label} (+${callingCode})`,
                    value: c.value,
                    code: c.value,
                    callingCode,
                    flag: `https://flagcdn.com/w40/${c.value.toLowerCase()}.png`
                };
            });
    }, []);

    useEffect(() => {
        const storedData = sessionStorage.getItem("demoAccountData");
        if (storedData) {
            setIsSubmitted(true);
        }
    }, []);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        const fieldError = validateField(name, value);
        setFormErrors((prevErrors) => ({
            ...prevErrors,
            ...fieldError,
            [name]: fieldError[name] || "",
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormValues({ ...formValues, [name]: checked });
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevents the default form submission behavior
        if (isSubmitting) return;
        const errors = validate();
        setFormErrors(errors);

        if (Object.keys(errors).length !== 0) return;
        setIsSubmitting(true);
        try {
            let params = {
                login_type: "api",
                ...formValues,
                country: formValues.country,
            };
            const { data } = await DemoLoginApi(params);

            if (data) {
                setIsSubmitted(true)
                setIsSubmitting(true);
                sessionStorage.setItem("demoAccountData", JSON.stringify(data));
                toast.success("Account Created successfully! Please Login");
            } else {
                toast.error("Account Creation failed");
            }
        } catch (error) {
            if (error?.response?.data?.message) {
                toast.error(error?.response?.data?.message);
            } else {
                toast.error("An error occurred while logging in.");
            }
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleCountryChange = (selectedOption) => {
        if (!selectedOption) return;

        const newCountryLabel = selectedOption.label || "";
        const newCountryValue = selectedOption.value || "";

        console.log("Label:", newCountryLabel, "Code:", newCountryValue);

        setFormValues((prev) => ({
            ...prev,
            country: newCountryValue, // Save as value like "IN"
        }));

        const countryErrorObj = validateField("country", newCountryValue);

        let phoneError = "";
        if (formValues.phone) {
            try {
                const phoneNumber = parsePhoneNumberFromString(formValues.phone, newCountryValue);
                if (!phoneNumber || !phoneNumber.isValid()) {
                    phoneError = "Invalid phone number for selected country";
                } else {
                    const len = phoneNumber.nationalNumber.length;
                    if (len < 7 || len > 15) {
                        phoneError = "Phone number must be between 7 and 15 digits";
                    }
                }
            } catch {
                phoneError = "Invalid phone number format";
            }
        }

        setFormErrors((prev) => ({
            ...prev,
            country: countryErrorObj.country || "",
            phone: phoneError,
        }));
    };

    const handleCompanyClick = () => {
        setIsCompanyDetailsVisible(!isCompanyDetailsVisible);
    };

    const validate = () => {
        const errors = {};
        if (!formValues.name.trim()) {
            errors.name = "Name is required";
        } else if (formValues.name.trim().length < 4) {
            errors.name = "Name must be at least 4 characters";
        } if (!formValues.email.trim()) errors.email = "Email is required";
        if (!formValues.phone.trim()) {
            errors.phone = "Phone number is required";
        } else if (formValues.country) {
            const selectedCountry = countries.find((option) => option.value === formValues.country);
            const countryCode = selectedCountry?.value;

            try {
                const parsedPhone = parsePhoneNumberFromString(formValues.phone, countryCode);
                if (!parsedPhone || !parsedPhone.isValid()) {
                    errors.phone = "Invalid phone number for selected country";
                } else {
                    const nationalNumberLength = parsedPhone.nationalNumber.length;
                    if (nationalNumberLength < 7 || nationalNumberLength > 15) {
                        errors.phone =
                            "Phone number must be between 7 and 15 digits";
                    }
                }
            } catch (err) {
                errors.phone = "Invalid phone number format";
            }
        }
        if (!formValues.address.trim()) errors.address = "Address is required";
        if (typeof formValues.country !== "string" || !formValues.country.trim()) errors.country = "Country is required";
        if (!formValues.deposit.trim()) errors.deposit = "Deposit is required";
        return errors;
    };

    const validateField = (name, value) => {
        const error = {};
        if (typeof value !== "string") {
            value = String(value || "");
        }

        switch (name) {
            case "name":
                if (!value.trim()) error.name = "Name is required";
                else if (value.trim().length < 4) error.name = "Name must be at least 4 characters";
                break;

            case "email":
                if (!value.trim()) {
                    error.email = "Email is required";
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error.email = "Invalid email format";
                }
                break;

            case "phone":
                if (!value.trim()) error.phone = "Phone number is required";
                else if (formValues.country) {
                    const selectedCountry = countries.find(opt => opt.label === formValues.country);
                    const countryCode = selectedCountry?.value;
                    try {
                        const parsedPhone = parsePhoneNumberFromString(value, countryCode);
                        if (!parsedPhone || !parsedPhone.isValid()) {
                            error.phone = "Invalid phone number for selected country";
                        } else {
                            const len = parsedPhone.nationalNumber.length;
                            if (len < 7 || len > 15) {
                                error.phone = "Phone number must be between 7 and 15 digits";
                            }
                        }
                    } catch {
                        error.phone = "Invalid phone number format";
                    }
                }
                break;

            case "address":
                if (!value.trim()) error.address = "Address is required";
                break;

            case "country":
                if (!value || typeof value !== "string" || !value.trim()) {
                    error.country = "Country is required";
                }
                break;

            case "deposit":
                if (!value.trim()) error.deposit = "Deposit is required";
                break;

            default:
                break;
        }

        return error;
    };

    const isFormValid = () => {
        const errors = validate();
        return Object.keys(errors).length === 0;
    };

    return (
        isSubmitted ? (
            <UserDetail />
        ) : (
            <form onSubmit={handleSubmit}>
                <Grid container spacing={1.5} sx={{ padding: 0 }} alignItems="center" >
                    <Grid item xs={1}><MdOutlineKeyboardBackspace onClick={back} style={{ fontSize: '1.5rem', color: 'var(--primary)', cursor: 'pointer' }} /></Grid>
                    <Grid item xs={11}><Typography variant="h5" fontWeight={700} sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t("openDemoAccount")}</Typography></Grid>
                    <Grid item xs={3}><Typography textAlign='right' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('company')} </Typography></Grid>
                    <Grid item xs={9}><Typography textAlign='left' variant="body2" className="text-primary" onClick={handleCompanyClick}>{process.env.REACT_APP_COMPANY_NAME}</Typography></Grid>

                    <Grid item xs={3}><Typography textAlign='right' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('Name')}</Typography></Grid>
                    <Grid item xs={9}><TextField error={!!formErrors.name} helperText={formErrors.name} placeholder={t('Name')} name="name" variant="outlined" fullWidth size="small" value={formValues.name} onChange={handleInputChange} className="custom-textfield" /></Grid>
                    <Grid item xs={3}> <Typography textAlign='right' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('email')} </Typography></Grid>
                    <Grid item xs={9}><TextField error={!!formErrors.email} helperText={formErrors.email} name="email" variant="outlined" fullWidth placeholder={t('email')} size="small" value={formValues.email} onChange={handleInputChange} className="custom-textfield" /></Grid>
                    <Grid item xs={3}><Typography textAlign='right' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('address')} </Typography> </Grid>
                    <Grid item xs={9}><TextField error={!!formErrors.address} helperText={formErrors.address} placeholder={t('address')} name="address" variant="outlined" fullWidth size="small" value={formValues.address} onChange={handleInputChange} className="custom-textfield" /></Grid>

                    <Grid item xs={3}><Typography textAlign='right' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t("country")}</Typography></Grid>
                    <Grid item xs={9}><ContrySelect placeholder={t('selectcountry')} options={countries} value={countries.find(option => option.label === formValues.country)} onChange={handleCountryChange} /></Grid>

                    <Grid item xs={3}><Typography textAlign='right' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t("PhoneNumber")}</Typography> </Grid>
                    <Grid item xs={9}><PhoneNumberInput formValues={formValues} formErrors={formErrors} setFormValues={setFormValues} setFormErrors={setFormErrors} countries={countries} /></Grid>

                    <Grid item xs={3}><Typography textAlign='right' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('deposit')}</Typography></Grid>
                    <Grid item xs={9}><TextField error={!!formErrors.deposit} helperText={formErrors.deposit} sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }} name="deposit" variant="outlined" size="small" fullWidth value={formValues.deposit} onChange={handleInputChange} className="custom-textfield" /></Grid>

                    <Grid item xs={12} textAlign='left'>
                        <FormControlLabel control={
                            <Checkbox name="agree" checked={formValues.agree} onChange={handleCheckboxChange} className="custom-checkbox" style={{ marginLeft: '1rem' }} />}
                            label={t('agreeText')}
                            sx={{ "& .MuiFormControlLabel-label": { fontSize: "10px", }, color: darkMode ? "var(--lightText)" : 'var(--black)' }} />
                    </Grid>

                    <Grid item xs={12}>
                        <Button fullWidth disabled={!isFormValid() || isSubmitting} type="submit" variant="contained" color="secondary"
                            //  sx={{
                            //     marginTop: 2, py: 1.2, borderRadius: 2, textTransform: "none", color: '#fff', cursor: !isFormValid() ? 'not-allowed' : 'pointer',
                            //     backgroundImage: !isFormValid() ? 'none' : darkMode ? 'linear-gradient(to bottom, #1e293b, #0f1720)' : 'linear-gradient(to bottom, #232323, #0f0f0f)', border: !isFormValid() ? '1px solid green' : 'none',
                            //     boxShadow: !isFormValid() ? 'none' : darkMode ? '0 2px 4px rgba(0,0,0,0.6)' : '0 2px 4px rgba(0,0,0,0.2)', '&:hover': { backgroundImage: !isFormValid() ? 'none' : darkMode ? 'linear-gradient(to bottom, #334155, #1e293b)' : 'linear-gradient(to bottom, #3a3a3a, #2a2a2a)', boxShadow: !isFormValid() ? 'none' : darkMode ? '0 3px 6px rgba(0,0,0,0.7)' : '0 3px 6px rgba(0,0,0,0.3)', },
                            // }}>
                            sx={{
                                marginTop: 2, py: 1.2, borderRadius: 2, textTransform: "none", color: '#fff', cursor: !isFormValid() ? 'not-allowed' : 'pointer', border: !isFormValid() ? '1px solid green' : 'none',
                                boxShadow: !isFormValid() ? 'none' : '0 2px 4px rgba(0,0,0,0.2)', '&:hover': { boxShadow: !isFormValid() ? 'none' : '0 3px 6px rgba(0,0,0,0.3)', },
                            }}>
                            {isSubmitting ? t('submitting') : t('openDemoAccount')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        )
    );
};

export default SignUp;